import { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import MenuIcon from '@mui/icons-material/Menu';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import CheckIcon from '@mui/icons-material/Check';
import ListItemText from '@mui/material/ListItemText';
import { themeCatalogue } from '@/theme/theme';
import { useNavigate } from 'react-router-dom';
import { useUiStore } from '@/store/uiStore';
import { useAuthStore } from '@/auth/authStore';
import { logout } from '@/api/auth';
import NotificationBell from '@/components/notifications/NotificationBell';
import GlobalSearch from '@/components/search/GlobalSearch';
import { useBranding } from '@/theme/useBranding';
import LanguageMenu from '@/components/common/LanguageMenu';
import { useT } from '@/i18n/i18n';
import { useLanguageBootstrap } from '@/api/uiLanguages';

const appName = import.meta.env.VITE_APP_NAME ?? 'Annova ERP Plus';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
}

export default function Header() {
  const t = useT();
  const colorMode = useUiStore((s) => s.colorMode);
  const branding = useBranding();
  const toggleColorMode = useUiStore((s) => s.toggleColorMode);
  const themeName = useUiStore((s) => s.themeName);
  const setThemeName = useUiStore((s) => s.setThemeName);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const user = useAuthStore((s) => s.user);
  useLanguageBootstrap(!!user);
  const navigate = useNavigate();

  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [themeAnchor, setThemeAnchor] = useState<null | HTMLElement>(null);

  const handleLogout = async () => {
    setAnchor(null);
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Toolbar>
        <IconButton edge="start" color="inherit" onClick={toggleSidebar} aria-label="Toggle navigation" sx={{ mr: 1 }}>
          <MenuIcon />
        </IconButton>
        {branding?.logoDataUrl && <Box component="img" src={branding.logoDataUrl} alt="" sx={{ height: 32, maxWidth: 140, objectFit: 'contain', mr: 1.5, display: { xs: 'none', sm: 'block' } }} />}
        <Typography variant="h6" noWrap sx={{ fontWeight: 600, letterSpacing: 0.2, display: { xs: 'none', sm: 'block' } }}>
          {branding?.organisationName ?? appName}
        </Typography>

        {/* Outlook-style centred search box: one query across everything the person may see. */}
        <GlobalSearch />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: { xs: 'auto', md: 0 } }}>
          <NotificationBell />
          <Tooltip title='Theme'>
            <IconButton color='inherit' onClick={(e) => setThemeAnchor(e.currentTarget)} aria-label='Choose theme'>
              <PaletteOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={themeAnchor}
            open={Boolean(themeAnchor)}
            onClose={() => setThemeAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { minWidth: 300, mt: 0.5 } } }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Theme</Typography>
              <Typography variant="caption" color="text.secondary">Applies to this browser; light and dark modes work with either.</Typography>
            </Box>
            <Divider />
            {themeCatalogue.map((option) => (
              <MenuItem
                key={option.id}
                selected={option.id === themeName}
                onClick={() => { setThemeName(option.id); setThemeAnchor(null); }}
                sx={{ alignItems: 'flex-start', py: 1 }}
              >
                <ListItemIcon sx={{ mt: 0.25 }}>{option.id === themeName ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
                <ListItemText primary={option.label} secondary={option.description} secondaryTypographyProps={{ sx: { whiteSpace: 'normal' } }} />
              </MenuItem>
            ))}
          </Menu>
          <Tooltip title={colorMode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
            <IconButton color="inherit" onClick={toggleColorMode} aria-label="Toggle colour mode">
              {colorMode === 'light' ? <DarkModeOutlinedIcon /> : <LightModeOutlinedIcon />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Account">
            <IconButton color="inherit" onClick={(e) => setAnchor(e.currentTarget)} aria-label="Account menu" sx={{ ml: 0.5 }}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: 'secondary.main', fontSize: 14 }}>
                {user ? initials(user.fullName) : '?'}
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={anchor}
            open={Boolean(anchor)}
            onClose={() => setAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { minWidth: 240, mt: 0.5 } } }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {user?.fullName ?? 'User'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {user?.email}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {user?.roles.join(', ')}
              </Typography>
            </Box>
            <Divider />
            <MenuItem
              onClick={() => {
                setAnchor(null);
                navigate('/change-password');
              }}
            >
              <ListItemIcon>
                <LockResetOutlinedIcon fontSize="small" />
              </ListItemIcon>
              {t('Change password')}
            </MenuItem>
            <LanguageMenu onDone={() => setAnchor(null)} />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutOutlinedIcon fontSize="small" />
              </ListItemIcon>
              {t('Sign out')}
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
